import {
  LOCALSTORAGE_FIRST_ORDER_FORM_COLLECTIONS,
  LOCALSTORAGE_TAGS,
} from '@constants/localStorage';
import { useAuthentication } from '@context/businessContainer';
import {
  COLLECTIONS_PREFERENCES_DOCUMENT_NAME,
  FEATURE_FLAG_WEB_FIRST_ORDER_FORM,
  PREFERRED_PREFERENCES_COLLECTION_NAME,
} from '@monofront/business/constants';
import {
  DeliveryBarMachineContext,
  useEligibility,
  useFeatureIsOn,
} from '@monofront/business/contexts';
import { useLocalStorage } from '@monofront/business/hooks';
import {
  createMultiStepFormMachine,
  MultiStepFormStep,
} from '@monofront/business/stateMachines';
import {
  Context,
  Events,
  AddressFormEvent,
} from '@monofront/business/stateMachines/multiStepForm/types';
import { useStateRef } from '@monofront/business/utils';
import { trackEvent } from '@monofront/tracking';
import CustomError from '@screens/CustomError';
import {
  createOrGetPreferences,
  saveUserPreferences,
} from '@services/userPreferences';
import { useMachine } from '@monofront/xstate-v4/react';
import { useRouter } from 'next/router';
import { Dispatch, SetStateAction, useContext, useMemo, useState } from 'react';
import { createContext } from 'react';
import { FieldValues, UseFormReturn, useForm } from 'react-hook-form';
import {
  BaseActionObject,
  EventData,
  ResolveTypegenMeta,
  SCXML,
  ServiceMap,
  SingleOrArray,
  State,
  TypegenDisabled,
} from '@monofront/xstate-v4';
import { addOmsTags, removeOmsTags } from '@services/user';
import { EVENTS } from '@monofront/business/stateMachines/deliveryBar';
interface IFirstOrderFormContextProps {
  children?: React.ReactNode;
  steps: Record<string, MultiStepFormStep>;
  form: UseFormReturn<FieldValues>;
  state: State<
    Context,
    Events,
    any,
    {
      value: any;
      context: Context;
    },
    ResolveTypegenMeta<TypegenDisabled, Events, BaseActionObject, ServiceMap>
  >;
  send: (
    event: SCXML.Event<Events> | SingleOrArray<any>,
    payload?: EventData,
  ) => State<Context, Events, any, any>;
  isValidating: boolean;
  setIsValidating: Dispatch<SetStateAction<boolean>>;
  currentStep: MultiStepFormStep;
}

const FirstOrderFormContext = createContext<IFirstOrderFormContextProps>({
  steps: {},
  form: {} as UseFormReturn<FieldValues>,
  state: {} as State<
    Context,
    Events,
    any,
    {
      value: any;
      context: Context;
    },
    ResolveTypegenMeta<TypegenDisabled, Events, BaseActionObject, ServiceMap>
  >,
  send: {} as (
    event: SCXML.Event<Events> | SingleOrArray<any>,
    payload?: EventData,
  ) => State<Context, Events, any, any>,
  isValidating: false,
  setIsValidating: () => ({} as Dispatch<SetStateAction<boolean>>),
  currentStep: {} as MultiStepFormStep,
});

const FirstOrderFormProvider: React.FC<{
  children: React.ReactNode;
  content;
}> = ({ children, content }) => {
  const router = useRouter();
  const [isValidating, setIsValidating] = useState(true);
  const { customer, isCustomerLoading } = useAuthentication();
  const isFirstOrderFormABTestEnabled = useFeatureIsOn(
    FEATURE_FLAG_WEB_FIRST_ORDER_FORM,
  );
  const { checkEligibility } = useEligibility();
  const [, setLocalTags] = useLocalStorage(
    LOCALSTORAGE_TAGS,
    [] as Array<string>,
  );
  const [collections, setCollections] = useLocalStorage(
    LOCALSTORAGE_FIRST_ORDER_FORM_COLLECTIONS,
    [] as Array<string>,
  );
  const form = useForm({
    mode: 'onChange',
  });
  const { send: sendToDeliveryBarMachine } =
    DeliveryBarMachineContext.useActorRef();

  const customerStateRef = useStateRef(customer);

  const machine = useMemo(() => {
    if (isCustomerLoading) {
      return null;
    }

    return createMultiStepFormMachine({
      id: 'firstOrderForm',
      steps: Object.values(content.steps),
      customer: customerStateRef.current ?? null,
      onFormStarted: () => {
        trackEvent({
          type: 'LFStartForm',
          data: {
            formName: '1st order form',
          },
        });
      },
      onFormStepSubmitted: ({
        stepNumber,
        stepName,
        answersSelected,
        displayedAnswers,
        collectionsToSave,
      }) => {
        if (customerStateRef.current) {
          saveUserPreferences(
            customerStateRef.current.uid,
            COLLECTIONS_PREFERENCES_DOCUMENT_NAME,
            {
              [PREFERRED_PREFERENCES_COLLECTION_NAME]: collectionsToSave,
            },
          );
        } else {
          setCollections(collectionsToSave);
        }

        window.scrollTo(0, 0); // scroll to top after each step

        trackEvent({
          type: 'LFSubmitFormStep',
          data: {
            formName: '1st order form',
            formStepNumber: stepNumber,
            formStepName: stepName,
            answersSelected: answersSelected,
            ...displayedAnswers,
          },
        });
      },
      onFormFinished: () => {
        trackEvent({
          type: 'LFEndForm',
          data: {
            formName: '1st order form',
          },
        });
        const redirectLink = isFirstOrderFormABTestEnabled
          ? '/first-order-form/congrats'
          : '/home';

        router.push(redirectLink);
      },
      onFormExited: () => {
        router.push('/home');
      },
      addTag: (tag) => {
        customer && addOmsTags(customer.uid, tag);

        setLocalTags((tags) => {
          if (!tags.includes(tag)) {
            tags.push(tag);
          }

          return tags;
        });
      },
      removeTag: (tag) => {
        customer && removeOmsTags(customer.uid, tag);

        setLocalTags((tags) => {
          let newTags = tags;

          const index = newTags.indexOf(tag);

          if (index > -1) {
            newTags = tags.splice(index, 1);
          }

          return newTags;
        });
      },
      getCollectionPreferences: async () => {
        if (!customerStateRef.current) {
          return collections || [];
        }

        const preferences = await createOrGetPreferences(
          customerStateRef.current.uid,
          COLLECTIONS_PREFERENCES_DOCUMENT_NAME,
        );

        if (!preferences) return [];

        return (
          preferences[PREFERRED_PREFERENCES_COLLECTION_NAME] ||
          collections ||
          []
        );
      },
      checkEligibility,
      handleAddressForm: (address: AddressFormEvent) => {
        const { postcode: _postcode, ...rest } = address;

        sendToDeliveryBarMachine({
          type: EVENTS.OVERWRITE_ADDRESS,
          address: {
            ...rest,
            postalCode: address.postcode,
          },
        });
      },
    });
  }, [setLocalTags, checkEligibility, isCustomerLoading]);

  if (!machine) {
    return null;
  }

  return (
    <ComponentWithMachine
      machine={machine}
      content={content}
      form={form}
      setIsValidating={setIsValidating}
      isValidating={isValidating}
    >
      {children}
    </ComponentWithMachine>
  );
};

const ComponentWithMachine = ({
  machine,
  content,
  form,
  isValidating,
  setIsValidating,
  children,
}) => {
  const [state, send] = useMachine(machine);

  if (!content || !machine) {
    return <CustomError statusCode={404} />;
  }

  return (
    <FirstOrderFormContext.Provider
      value={{
        steps: content.steps,
        form,
        state: state,
        send: send as any,
        isValidating,
        setIsValidating,
        currentStep: content.steps[state.value as keyof typeof content.steps],
      }}
    >
      {children}
    </FirstOrderFormContext.Provider>
  );
};

const useFirstOrderFormContext = () => useContext(FirstOrderFormContext);

export { useFirstOrderFormContext, FirstOrderFormProvider };
